import { useState, useCallback, useRef } from 'react';
import GlobalStyles from './GlobalStyles';
import ScrollProgressBar from './components/ScrollProgressBar';
import Header from './components/Header';
import { Intro, QuiSom, QueFem, OnSom, Esplac, Contacte } from './components/Sections';
import Galeria from './components/Galeria';
import AdminBar from './components/AdminBar';
import { PasswordModal, AdminModal, EditModal } from './components/Modals';
import { useGaleria, useContingut, useReveal } from './hooks';
import { PASSWORDS, C } from './config/constants';

function AppContent({ contingut, galeria, setGaleria, saveGaleria, saveContingut, setContingut }) {
  useReveal();

  const [isAdmin,    setIsAdmin]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [pwModal,    setPwModal]    = useState({ open:false, key:null });
  const [adminModal, setAdminModal] = useState(false);
  const [editModal,  setEditModal]  = useState({ open:false, config:null, onSave:null, onDelete:null });
  const saveTimer = useRef(null);

  const fotos = contingut.queFem?.fotos || [];

  const updateAndSave = useCallback((next) => {
    setGaleria(next); saveGaleria(next);
  }, [setGaleria, saveGaleria]);

  const handleContingutChange = useCallback((seccio, val) => {
    const next = { ...contingut, [seccio]: val };
    setContingut(next);
    setSaving(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      await saveContingut(next); setSaving(false);
    }, 800);
  }, [contingut, setContingut, saveContingut]);

  const closeEdit = () => setEditModal(p => ({ ...p, open:false }));
  const upd = s => v => handleContingutChange(s, v);

  const handleSeccioClick = key => setPwModal({ open:true, key });

  const handlePasswordValidate = input => {
    const { key } = pwModal;
    for (const curs in galeria) {
      if (galeria[curs][key]) {
        if (input === PASSWORDS.public) {
          setPwModal({ open:false, key:null });
          window.open(galeria[curs][key].link, '_blank');
        } else alert('Contrasenya incorrecta.');
        return;
      }
    }
    alert('Enllaç no trobat.');
  };

  const handleAdminValidate = input => {
    if (input === PASSWORDS.admin) { setIsAdmin(true); setAdminModal(false); }
    else alert('Contrasenya incorrecta.');
  };

  const handleAfegirCurs = () => {
    const any = prompt('Any inicial (ex: 2024)');
    if (!any || isNaN(any)) { alert('Any invàlid.'); return; }
    const et = `Curs ${any}–${parseInt(any)+1}`;
    if (galeria[et]) { alert('Ja existeix.'); return; }
    updateAndSave({ ...galeria, [et]:{} });
  };

  const handleAfegirSeccio = curs => {
    setEditModal({
      open:true, config:{ titol:'Afegir secció', nom:'', link:'' },
      onSave:(nom,link) => {
        updateAndSave({ ...galeria, [curs]:{ ...galeria[curs], [`k_${Date.now()}`]:{ nom,link } }});
        closeEdit();
      }, onDelete:null,
    });
  };

  const handleEditarSeccio = (curs, key) => {
    const e = galeria[curs]?.[key];
    if (!e) { alert('No trobat.'); return; }
    setEditModal({
      open:true, config:{ titol:'Editar secció', nom:e.nom, link:e.link },
      onSave:(nom,link) => {
        const s={...galeria[curs]}; delete s[key]; s[`k_${Date.now()}`]={ nom,link };
        updateAndSave({ ...galeria, [curs]:s }); closeEdit();
      },
      onDelete:() => {
        const s={...galeria[curs]}; delete s[key];
        updateAndSave({ ...galeria, [curs]:s }); closeEdit();
      },
    });
  };

  return (
    <>
      <ScrollProgressBar/>
      <Header onAdminClick={() => setAdminModal(true)}/>

      {/* Cada secció amb el seu color pla — simple i robust, sense degradats */}
      <div style={{ background: '#fff8e9' }}>
        <Intro data={contingut.intro} isAdmin={isAdmin} onChange={upd('intro')}/>
      </div>
      <div style={{ background: '#ffffff' }}>
        <QuiSom data={contingut.quiSom} isAdmin={isAdmin} onChange={upd('quiSom')} fotos={fotos}/>
      </div>
      <div style={{ background: C.grocClar }}>
        <QueFem data={contingut.queFem} isAdmin={isAdmin} onChange={upd('queFem')}/>
      </div>
      <div style={{ background: '#ffffff' }}>
        <OnSom data={contingut.onSom} isAdmin={isAdmin} onChange={upd('onSom')}/>
      </div>
      <div style={{ background: C.blauClar }}>
        <Esplac data={contingut.esplac} isAdmin={isAdmin} onChange={upd('esplac')} bgFoto={fotos[3]}/>
      </div>

      <div style={{ background: C.blau }}>
        <Contacte data={contingut.contacte} isAdmin={isAdmin} onChange={upd('contacte')} bgFoto={fotos[4]}/>
        <Galeria
          galeria={galeria} isAdmin={isAdmin}
          onSeccioClick={handleSeccioClick}
          onAfegirSeccio={handleAfegirSeccio}
          onEditarSeccio={handleEditarSeccio}
          onAfegirCurs={handleAfegirCurs}
        />
        <footer style={{
          padding: '2.5rem 1.5rem',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem',
        }}>
          <img src="assets/logo.png" alt="Logo" style={{ height: '42px', opacity: 0.85 }}/>
          <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)' }}>
            &copy; {contingut.footer?.text}
          </p>
        </footer>
      </div>

      {isAdmin && <AdminBar onLogout={() => setIsAdmin(false)} saving={saving}/>}

      <PasswordModal isOpen={pwModal.open}
        onClose={() => setPwModal({ open:false, key:null })}
        onValidate={handlePasswordValidate}/>
      <AdminModal isOpen={adminModal}
        onClose={() => setAdminModal(false)}
        onValidate={handleAdminValidate}/>
      <EditModal isOpen={editModal.open} onClose={closeEdit}
        config={editModal.config} onSave={editModal.onSave} onDelete={editModal.onDelete}/>
    </>
  );
}

export default function App() {
  const { galeria, setGaleria, saveGaleria }       = useGaleria();
  const { contingut, setContingut, saveContingut } = useContingut();

  if (!contingut) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
      height:'100vh', background:'#fff', flexDirection:'column', gap:'1rem' }}>
      <GlobalStyles/>
      <img src="assets/logo.png" alt="Logo" style={{ height:'64px', animation:'float 2s ease-in-out infinite' }}/>
      <span style={{ color:C.blau, fontWeight:700 }}>Carregant...</span>
    </div>
  );

  return (
    <>
      <GlobalStyles/>
      <AppContent
        contingut={contingut} setContingut={setContingut} saveContingut={saveContingut}
        galeria={galeria} setGaleria={setGaleria} saveGaleria={saveGaleria}
      />
    </>
  );
}